import { VOLATILITY_PERIODS } from "./volatility";
// 원본: OfficeJS ValuationPage. API 필드명과 조건을 유지하고 표시 단위는 웹 폼에서 변환한다.
export interface RcpsFieldDef {
  key: string;
  label: string;
  type: "text" | "number" | "date" | "select" | "bool";
  /** select용 옵션. 문자열만 주면 value=label로 사용 */
  options?: Array<string | { value: string; label: string }>;
  hint?: string;
  required?: boolean;
  /** 입력 우측에 표시되는 default/placeholder 힌트 (DS의 info-tooltip) */
  defaultHint?: string;
  /** 입력 단위 — % / 원 / 년 등 (라벨 뒤에 표시) */
  unit?: string;
  /** 다른 입력값에 따라 이 필드를 표시할지 결정 (예: 노드계산방법에 따라 노드개수/노드간격 전환) */
  showIf?: (params: Record<string, string>) => boolean;
  /** 조건에 따라 칸을 고정 — 보이되 바꿀 수 없고(value 로 표시), 검사·전송에서 빠진다. hint 로 이유를 알린다. */
  lock?: (params: Record<string, string>) => { value: string; hint: string } | undefined;
}

/**
 * 신용등급 옵션 — KOFIA/SEIBRO API 와 호환되는 코드명.
 *   value 는 API 에 그대로 전송 (AA0, A0, BBB0 등이 KOFIA/SEIBRO 내부 매핑 키).
 *   label 은 사용자에게 자연스러운 표기.
 *   "custom" 선택 시 사용자가 직접 등급을 텍스트로 입력 가능.
 */
const RCPS_CREDIT_GRADE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "AAA", label: "AAA" },
  { value: "AA+", label: "AA+" },
  { value: "AA0", label: "AA" },
  { value: "AA-", label: "AA-" },
  { value: "A+", label: "A+" },
  { value: "A0", label: "A" },
  { value: "A-", label: "A-" },
  { value: "BBB+", label: "BBB+" },
  { value: "BBB0", label: "BBB" },
  { value: "BBB-", label: "BBB-" },
];

/** 부트스트래핑 출처 — 자동조회용 */
const RCPS_RATE_SOURCE_OPTIONS = [
  { value: "seibro", label: "seibro" },
  { value: "kofia", label: "kofia" },
];

export interface RcpsSection {
  id: string;
  step: number;
  title: string;
  desc?: string;
  /** true면 헤더 우측에 ON/OFF 토글이 표시되고, toggleKey 필드가 0/1을 컨트롤 */
  toggleable?: boolean;
  toggleKey?: string;
  rows: RcpsFieldDef[][];
}

export const RCPS_SECTIONS: RcpsSection[] = [
  {
    id: "company",
    step: 1,
    title: "프로젝트명",
    rows: [
      [
        {
          key: "valuation_company",
          label: "프로젝트명",
          type: "text",
          // 선택 — 비워 두면 계산할 때 이 이름으로 채운다(runEvaluation)
          defaultHint: "DeepSearch 프로젝트",
        },
      ],
    ],
  },
  {
    id: "model",
    step: 2,
    title: "평가모델",
    desc: "복합상품을 평가하기 위한 평가모델을 선정합니다. TF모델과 GS모델을 지원합니다.",
    rows: [
      [
        {
          key: "valuation_model",
          label: "평가방법",
          type: "select",
          options: [
            { value: "TF", label: "TF(Tsiveriotis-Fernandes)" },
            { value: "GS", label: "GS(Goldman Sachs)" },
          ],
          required: true,
        },
      ],
    ],
  },
  {
    id: "node",
    step: 3,
    title: "노드 및 날짜 입력",
    desc: "복합상품의 평가기준일, 발행일, 만기일 및 노드계산방법에 대한 변수를 입력합니다.",
    rows: [
      [
        {
          key: "date_array_method",
          label: "노드계산방법",
          type: "select",
          options: [
            { value: "node", label: "노드개수기반" },
            { value: "interval", label: "일자간격기반" },
          ],
        },
        {
          key: "node_count",
          label: "노드개수",
          type: "number",
          defaultHint: "201",
          hint: "전체 구간을 몇 개의 노드로 나눌지",
          showIf: (p) => (p.date_array_method || "node") !== "interval",
        },
        {
          key: "node_interval",
          label: "노드간간격(일)",
          type: "number",
          defaultHint: "7",
          hint: "노드 사이 간격을 며칠로 할지",
          showIf: (p) => p.date_array_method === "interval",
        },
      ],
      [
        { key: "issue_date", label: "발행일", type: "date", required: true },
        { key: "maturity_date", label: "만기일", type: "date", required: true },
      ],
      [
        {
          key: "valuation_date",
          label: "평가기준일",
          type: "date",
          required: true,
        },
      ],
    ],
  },
  {
    id: "basic",
    step: 4,
    title: "기본사항",
    desc: "복합상품의 기초자산, 주당발행가격, 주당전환가격, 보통주 기대배당률을 입력합니다.",
    rows: [
      [
        {
          key: "current_stock_price",
          label: "주당 기초자산가격",
          type: "number",
          required: true,
          unit: "원",
          defaultHint: "-",
        },
        {
          key: "dividend_yield",
          label: "보통주 기대배당률",
          type: "number",
          required: true,
          unit: "%",
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.01 = 1%)",
        },
      ],
      [
        {
          key: "issue_price",
          label: "주당 발행가격",
          type: "number",
          required: true,
          unit: "원",
          defaultHint: "-",
        },
        {
          key: "conversion_price",
          label: "주당 전환가격",
          type: "number",
          required: true,
          unit: "원",
          defaultHint: "-",
        },
      ],
      [
        {
          key: "is_nominal_interest",
          label: "액면이자 반영여부",
          type: "select",
          options: [
            { value: "0", label: "미반영" },
            { value: "1", label: "반영" },
          ],
        },
        {
          key: "nominal_interest_rate",
          label: "명목이자율",
          type: "number",
          unit: "%",
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.02 = 2%)",
        },
      ],
      [
        {
          key: "nominal_interest_cycle",
          label: "이자지급주기",
          type: "number",
          defaultHint: "-",
        },
        {
          key: "nominal_interest_cycle_interval",
          label: "이자지급주기 단위",
          type: "select",
          options: [
            { value: "year", label: "년" },
            { value: "month", label: "월" },
            { value: "day", label: "일" },
          ],
        },
      ],
    ],
  },
  {
    id: "conversion",
    step: 5,
    title: "전환권",
    desc: "복합상품의 전환권과 관련된 변수를 입력합니다.",
    rows: [
      [
        {
          key: "conversion_start_date",
          label: "전환청구 시작일",
          type: "date",
          required: true,
        },
        {
          key: "conversion_end_date",
          label: "전환청구 종료일",
          type: "date",
          required: true,
        },
      ],
      [
        {
          key: "conversion_cycle",
          label: "전환청구 주기",
          type: "number",
          defaultHint: "-",
        },
        {
          key: "conversion_cycle_interval",
          label: "전환청구 주기 단위",
          type: "select",
          options: [
            { value: "day", label: "일" },
            { value: "month", label: "월" },
            { value: "year", label: "년" },
          ],
        },
      ],
    ],
  },
  {
    id: "volatility",
    step: 6,
    title: "변동성 계산",
    desc: "주가변동성을 산출하기 위한 변수를 입력합니다.",
    rows: [
      [
        {
          key: "volatility_input",
          label: "변동성 입력방법",
          type: "select",
          options: ["직접입력", "유사회사선정"],
        },
        {
          key: "volatility",
          label: "연 변동성",
          type: "number",
          unit: "%",
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.30 = 30%)",
        },
      ],
      [
        {
          key: "volatility_conversion_days",
          label: "변동성 연환산 적용일",
          type: "number",
          unit: "일",
          defaultHint: "248일",
          hint: "1년 영업일수 (기본 248)",
        },
      ],
      [
        {
          key: "volatility_period",
          label: "주가 조회기간",
          type: "select",
          options: VOLATILITY_PERIODS,
          showIf: (p) => p.volatility_input === "유사회사선정",
        },
        {
          key: "volatility_date_from",
          label: "주가 조회 시작일",
          type: "date",
          required: true,
          showIf: (p) => p.volatility_input === "유사회사선정" && p.volatility_period === "custom",
        },
        {
          key: "stockprice_query_period",
          label: "주가 관측수",
          type: "number",
          unit: "개",
          required: true,
          hint: "평가기준일부터 거슬러 올라간 거래일 수",
          showIf: (p) => p.volatility_input === "유사회사선정" && p.volatility_period === "observations",
        },
      ],
    ],
  },
  {
    id: "redemption",
    step: 7,
    title: "상환권",
    desc: "복합상품의 상환권(조기상환청구권)과 관련된 변수를 입력합니다.",
    rows: [
      [
        {
          key: "redemption_start_date_dividend_eligible_earnings",
          label: "조기상환 시작일 (배당가능이익)",
          type: "date",
          required: true,
        },
        {
          key: "redemption_start_date_contract",
          label: "조기상환 시작일 (계약기준)",
          type: "date",
          required: true,
        },
      ],
      [
        {
          key: "redemption_end_date",
          label: "조기상환 종료일",
          type: "date",
          required: true,
        },
        {
          key: "redemption_claim_interest_rate",
          label: "조기상환 이자율",
          type: "number",
          required: true,
          unit: "%",
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.05 = 5%)",
        },
      ],
    ],
  },
  {
    id: "bootstrapping",
    step: 8,
    title: "부트스트래핑",
    desc: "무위험할인율(Rf), 위험할인율(Kd)의 YTM을 조회하고 현물 및 선도이자율을 계산합니다.",
    rows: [
      [
        {
          key: "interest_input_method",
          label: "이자율 입력방법",
          type: "select",
          options: ["자동입력", "직접입력"],
          hint: "자동입력 = 외부 조회 (seibro/KOFIA)",
        },
        {
          key: "rate_source",
          label: "출처",
          type: "select",
          options: RCPS_RATE_SOURCE_OPTIONS,
          defaultHint: "seibro",
          hint: "자동 금리조회 출처를 선택합니다.",
        },
      ],
      [
        {
          key: "credit_rate",
          label: "신용등급",
          type: "select",
          options: RCPS_CREDIT_GRADE_OPTIONS,
          defaultHint: "AA+",
        },
        {
          key: "rf_basis",
          label: "Rf 부트스트래핑 단위 (년)",
          type: "select",
          options: [
            { value: "0.25", label: "0.25" },
            { value: "0.5", label: "0.5" },
            { value: "1", label: "1" },
          ],
          defaultHint: "0.25",
        },
      ],
      [
        {
          key: "kd_basis",
          label: "Kd 부트스트래핑 단위 (년)",
          type: "select",
          options: [
            { value: "0.25", label: "0.25" },
            { value: "0.5", label: "0.5" },
            { value: "1", label: "1" },
          ],
          defaultHint: "0.25",
        },
      ],
    ],
  },
  {
    id: "diluted",
    step: 9,
    title: "희석효과",
    desc: "희석효과를 노드에 반영하는 경우, 전환될 주식수와 기발행주식수를 고려하여 희석효과를 반영합니다.",
    toggleable: true,
    toggleKey: "is_diluted",
    rows: [
      [
        {
          key: "outstanding_shares",
          label: "기발행주식수",
          type: "number",
          unit: "주",
          defaultHint: "-",
          hint: "평가기준일 현재 발행되어 있는 보통주 수",
        },
        {
          key: "conversion_shares",
          label: "전환주식수",
          type: "number",
          unit: "주",
          defaultHint: "-",
          hint: "RCPS 전환 시 발행될 보통주 수",
        },
      ],
    ],
  },
  {
    id: "ipoRefixing",
    step: 10,
    title: "IPO 리픽싱",
    desc: "IPO 리픽싱 조건을 반영하는 경우 변수를 입력합니다.",
    toggleable: true,
    toggleKey: "is_ipo_refixing",
    rows: [
      [
        {
          key: "ipo_refixing_base",
          label: "IPO리픽싱 기준",
          type: "select",
          options: ["공모가액", "확정공모가액"],
        },
        {
          key: "ipo_refixing_ratio",
          label: "IPO리픽싱 비율",
          type: "number",
          unit: "%",
          defaultHint: "-",
        },
      ],
      [
        {
          key: "ipo_refixing_start_date",
          label: "IPO리픽싱 시작일",
          type: "date",
        },
        {
          key: "ipo_refixing_end_date",
          label: "IPO리픽싱 종료일",
          type: "date",
        },
      ],
      [
        {
          key: "ipo_refixing_cycle",
          label: "IPO리픽싱 주기",
          type: "number",
          defaultHint: "-",
        },
        {
          key: "ipo_refixing_cycle_interval",
          label: "IPO리픽싱 주기 단위",
          type: "select",
          options: [
            { value: "month", label: "월" },
            { value: "day", label: "일" },
            { value: "year", label: "년" },
          ],
        },
      ],
    ],
  },
  {
    id: "stockRefixing",
    step: 11,
    title: "주가 리픽싱",
    desc: "주가리픽싱 조건을 반영하는 경우 변수를 입력하여 주가리픽싱효과를 반영합니다.",
    toggleable: true,
    toggleKey: "is_stockprice_refixing",
    rows: [
      [
        {
          key: "is_upward_refixing",
          label: "상향리픽싱 반영여부",
          type: "select",
          options: [
            { value: "0", label: "해당사항없음" },
            { value: "1", label: "반영" },
          ],
        },
        {
          key: "stockprice_refixing_minimum_conversion_price",
          label: "주가리픽싱 하한전환가격",
          type: "number",
          unit: "원",
          defaultHint: "-",
        },
      ],
      [
        {
          key: "stockprice_refixing_start_date",
          label: "주가리픽싱 시작일",
          type: "date",
        },
        {
          key: "stockprice_refixing_end_date",
          label: "주가리픽싱 종료일",
          type: "date",
        },
      ],
      [
        {
          key: "stockprice_refixing_cycle",
          label: "주가리픽싱 주기",
          type: "number",
          defaultHint: "-",
        },
        {
          key: "stockprice_refixing_cycle_interval",
          label: "주가리픽싱 주기 단위",
          type: "select",
          options: [
            { value: "month", label: "월" },
            { value: "day", label: "일" },
            { value: "year", label: "년" },
          ],
        },
      ],
      [
        {
          key: "stockprice_refixing_base_1",
          label: "가중산술평균주가 기준기간 (최근일)",
          type: "number",
          defaultHint: "-",
        },
      ],
      [
        {
          key: "stockprice_refixing_base_2",
          label: "가중산술평균주가 기준기간 (1주일)",
          type: "number",
          defaultHint: "1",
        },
      ],
      [
        {
          key: "stockprice_refixing_base_3",
          label: "가중산술평균주가 기준기간 (1개월)",
          type: "number",
          defaultHint: "4",
        },
      ],
    ],
  },
  {
    id: "callOption",
    step: 12,
    title: "수의상환권 (콜옵션 / 매도청구권)",
    desc: "복합상품의 수의상환권(발행자 또는 제3자)과 관련된 변수를 입력합니다.",
    toggleable: true,
    toggleKey: "is_calloption",
    rows: [
      [
        {
          key: "calloption_case",
          label: "수의상환권 케이스",
          type: "select",
          options: [
            { value: "발행자", label: "발행자" },
            { value: "제3자", label: "제3자" },
          ],
        },
        {
          key: "is_conversion_prevention",
          label: "전환방지조항 여부",
          type: "select",
          // 발행자 콜옵션에만 쓰인다 — 계산 엔진이 제3자에는 적용하지 않으므로(애드인 시트 메모 '제3자인 경우 전환방지조항 미적용')
          // 제3자면 칸은 보이되 '미반영'으로 고정하고 보내지 않는다(API 기본 0=미반영). 숨기면 칸이 사라져 헷갈린다(사용자 요청).
          lock: (p) => (p.calloption_case === "제3자" ? { value: "0", hint: "제3자 콜옵션에는 전환방지조항이 적용되지 않습니다" } : undefined),
          // 기본 미반영(0) — API·애드인 기본과 같게(사용자 결정)
          options: [
            { value: "0", label: "미반영" },
            { value: "1", label: "반영" },
          ],
        },
      ],
      [
        {
          key: "calloption_start_date",
          label: "수의상환권 시작일",
          type: "date",
        },
        {
          key: "calloption_end_date",
          label: "수의상환권 종료일",
          type: "date",
        },
      ],
      [
        {
          key: "calloption_cycle",
          label: "수의상환권 청구주기",
          type: "number",
          defaultHint: "-",
        },
        {
          key: "calloption_cycle_interval",
          label: "청구 주기 단위",
          type: "select",
          options: [
            { value: "month", label: "월" },
            { value: "day", label: "일" },
            { value: "year", label: "년" },
          ],
        },
      ],
      [
        {
          key: "calloption_interest_rate",
          label: "수의상환권 이자율",
          type: "number",
          unit: "%",
          defaultHint: "0%",
          hint: "소수 입력",
        },
        {
          key: "calloption_discount_method",
          label: "할인율 적용 방법",
          type: "select",
          options: [
            { value: "GS", label: "GS(Goldman Sachs 혼합할인율)" },
            { value: "TF", label: "TF(Tsiveriotis-Fernandes 혼합할인율)" },
          ],
        },
      ],
      [
        {
          key: "call_exercise_ratio",
          label: "수의상환권 행사비율",
          type: "number",
          unit: "%",
          defaultHint: "100%",
        },
      ],
      [
        {
          key: "calloption_ytc_method",
          label: "콜 YTC 계산방식",
          type: "select",
          options: [
            { value: "flat", label: "flat (단일 이자율)" },
            { value: "step_aware", label: "step_aware (스텝업 연동)" },
          ],
          hint: "step_aware 는 스텝업 쿠폰(아래 섹션)과 연동",
        },
        {
          key: "calloption_ytc_pre_stepup_rate",
          label: "스텝업 이전 보장수익률(YTC)",
          type: "number",
          unit: "%",
          defaultHint: "-",
          hint: "step_aware 모드 · 스텝업 시작일 이전 콜 행사 시. 소수 입력",
        },
      ],
      [
        {
          key: "calloption_ytc_interest_calculation_method",
          label: "콜옵션 YTC 이자 계산방식",
          type: "select",
          options: [
            { value: "복리", label: "복리" },
            { value: "단리", label: "단리" },
          ],
          defaultHint: "-",
          hint: "스텝업 콜옵션 보장수익률의 이자 계산 방식",
        },
        {
          key: "calloption_ytc_compound_period_months",
          label: "콜옵션 YTC 복리 주기(월)",
          type: "number",
          defaultHint: "-",
          hint: "복리 선택 시 이자 복리 주기 (예: 3 = 3개월)",
        },
      ],
    ],
  },
  {
    id: "stepup",
    step: 13,
    title: "스텝업 쿠폰 (Step-up)",
    desc: "신종자본증권·영구 CB 등 시간에 따라 이자율이 가산되는 스텝업/명시적 쿠폰 스케줄을 반영합니다. 비활성화 시 기존 단일 명목이자율(flat)을 사용합니다.",
    rows: [
      [
        {
          key: "coupon_schedule_method",
          label: "쿠폰 스케줄 방식",
          type: "select",
          options: [
            { value: "stepup", label: "stepup (최초이자율 + 가산)" },
            { value: "explicit", label: "explicit (날짜·이자율 직접)" },
          ],
        },
      ],
      [
        {
          key: "stepup_first_date",
          label: "최초 이자율조정일",
          type: "date",
          hint: "[stepup] 이 날 전까지 최초이자율 적용",
        },
        {
          key: "stepup_initial_rate",
          label: "최초이자율",
          type: "number",
          unit: "%",
          defaultHint: "상환청구 이자율",
          hint: "소수 입력 (미입력 시 상환청구 이자율)",
        },
      ],
      [
        {
          key: "stepup_first_increment",
          label: "첫 가산폭",
          type: "number",
          unit: "%",
          defaultHint: "-",
          hint: "[stepup] 예: 0.025 = +2.5%p",
        },
        {
          key: "stepup_annual_increment",
          label: "연간 가산폭",
          type: "number",
          unit: "%",
          defaultHint: "-",
          hint: "[stepup] 첫 가산 이후 매주기 균등 가산. 예: 0.005",
        },
      ],
      [
        {
          key: "stepup_annual_period_months",
          label: "가산주기 (월)",
          type: "number",
          defaultHint: "12",
          hint: "기본 12 = 매년",
        },
        {
          key: "stepup_max_rate",
          label: "이자율 상한(캡)",
          type: "number",
          unit: "%",
          defaultHint: "없음",
          hint: "[옵션] 예: 0.10 = 10% 상한",
        },
      ],
      [
        {
          key: "stepup_annual_increment_schedule",
          label: "연도별 가산스케줄",
          type: "text",
          defaultHint: "-",
          hint: "[옵션] 쉼표 구분 %. 예: 0.5,0.5,1 (우선순위 > 연간 가산폭)",
        },
      ],
      [
        {
          key: "coupon_schedule_dates",
          label: "쿠폰 적용일 목록",
          type: "text",
          defaultHint: "-",
          hint: "[explicit] 쉼표 구분 날짜. 예: 2026-01-01,2027-01-01",
        },
      ],
      [
        {
          key: "coupon_schedule_rates",
          label: "쿠폰 이자율 목록",
          type: "text",
          defaultHint: "-",
          hint: "[explicit] 쉼표 구분 %. 예: 2,2.5 (날짜와 길이 일치)",
        },
      ],
    ],
  },
];

export const STOCK_OPTION_SECTIONS: RcpsSection[] = [
  {
    id: "project",
    step: 1,
    title: "프로젝트명",
    rows: [
      [
        {
          key: "valuation_company",
          label: "프로젝트명",
          type: "text",
          // 선택 — 비워 두면 계산할 때 이 이름으로 채운다(runEvaluation)
          defaultHint: "DeepSearch 스톡옵션",
        },
      ],
    ],
  },
  {
    id: "node",
    step: 2,
    title: "노드 및 날짜 입력",
    desc: "스톡옵션 평가기준일, 부여일(발행일), 만기일 및 노드 계산방법을 입력합니다.",
    rows: [
      [
        {
          key: "valuation_date",
          label: "평가기준일",
          type: "date",
          required: true,
        },
        {
          key: "date_array_method",
          label: "노드계산방법",
          type: "select",
          options: [
            { value: "node", label: "노드개수기반" },
            { value: "interval", label: "일자간격기반" },
          ],
          required: true,
        },
      ],
      [
        { key: "issue_date", label: "부여일", type: "date", required: true },
        { key: "maturity_date", label: "만기일", type: "date", required: true },
      ],
      [
        {
          key: "node_count",
          label: "노드개수",
          type: "number",
          defaultHint: "201",
        },
        {
          key: "node_interval",
          label: "노드간격",
          type: "number",
          defaultHint: "0",
        },
      ],
    ],
  },
  {
    id: "basic",
    step: 3,
    title: "기초자산 및 행사가격",
    desc: "평가기준일 현재 주가, 부여 당시 주당가치, 행사가격과 기대배당률을 입력합니다.",
    rows: [
      [
        {
          key: "current_stock_price",
          label: "기초자산가격",
          type: "number",
          unit: "원",
          required: true,
          defaultHint: "-",
        },
        {
          key: "dividend_yield",
          label: "기대배당률",
          type: "number",
          unit: "%",
          required: true,
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.01 = 1%)",
        },
      ],
      [
        {
          key: "issue_price",
          label: "주당 발행가격",
          type: "number",
          unit: "원",
          required: true,
          defaultHint: "-",
        },
        {
          key: "conversion_price",
          label: "행사가격",
          type: "number",
          unit: "원",
          required: true,
          defaultHint: "-",
        },
      ],
    ],
  },
  {
    id: "exercise",
    step: 4,
    title: "행사조건",
    desc: "계약서상 행사 가능기간과 행사 주기를 입력합니다.",
    rows: [
      [
        {
          key: "conversion_start_date",
          label: "행사 시작일",
          type: "date",
          required: true,
        },
        {
          key: "conversion_end_date",
          label: "행사 종료일",
          type: "date",
          required: true,
        },
      ],
      [
        {
          key: "conversion_cycle",
          label: "행사 주기",
          type: "number",
          defaultHint: "0",
        },
        {
          key: "conversion_cycle_interval",
          label: "행사 주기 단위",
          type: "select",
          options: [
            { value: "day", label: "일" },
            { value: "month", label: "월" },
            { value: "year", label: "년" },
          ],
        },
      ],
    ],
  },
  {
    id: "volatility",
    step: 5,
    title: "변동성 계산",
    desc: "스톡옵션 평가를 위한 연 변동성을 직접 입력하거나 유사회사로 산출합니다.",
    rows: [
      [
        {
          key: "volatility_input",
          label: "변동성 입력방법",
          type: "select",
          options: ["직접입력", "유사회사선정"],
        },
        {
          key: "volatility",
          label: "연 변동성",
          type: "number",
          unit: "%",
          required: true,
          defaultHint: "0%",
          hint: "소수 입력 (예: 0.30 = 30%)",
        },
      ],
      [
        {
          key: "volatility_conversion_days",
          label: "변동성 연환산 적용일",
          type: "number",
          unit: "일",
          defaultHint: "248일",
        },
      ],
      [
        {
          key: "volatility_period",
          label: "주가 조회기간",
          type: "select",
          options: VOLATILITY_PERIODS,
          showIf: (p) => p.volatility_input === "유사회사선정",
        },
        {
          key: "volatility_date_from",
          label: "주가 조회 시작일",
          type: "date",
          required: true,
          showIf: (p) => p.volatility_input === "유사회사선정" && p.volatility_period === "custom",
        },
        {
          key: "stockprice_query_period",
          label: "주가 관측수",
          type: "number",
          unit: "개",
          required: true,
          hint: "평가기준일부터 거슬러 올라간 거래일 수",
          showIf: (p) => p.volatility_input === "유사회사선정" && p.volatility_period === "observations",
        },
      ],
    ],
  },
  {
    id: "bootstrapping",
    step: 6,
    title: "무위험이자율",
    desc: "무위험할인율(Rf) 조회/부트스트래핑 가정을 입력합니다.",
    rows: [
      [
        {
          key: "interest_input_method",
          label: "이자율 입력방법",
          type: "select",
          options: ["자동입력", "직접입력"],
        },
        {
          key: "rate_source",
          label: "출처",
          type: "select",
          options: [
            { value: "kofia", label: "kofia" },
            { value: "seibro", label: "seibro" },
          ],
          required: true,
          defaultHint: "kofia",
        },
      ],
      [
        {
          key: "rf_basis",
          label: "Rf 부트스트래핑 단위 (년)",
          type: "select",
          options: [
            { value: "0.25", label: "0.25" },
            { value: "0.5", label: "0.5" },
            { value: "1", label: "1" },
          ],
          defaultHint: "0.25",
        },
      ],
    ],
  },
  {
    id: "diluted",
    step: 7,
    title: "희석효과",
    desc: "희석효과를 반영할 경우 기발행주식수와 행사주식수를 입력합니다.",
    toggleable: true,
    toggleKey: "is_diluted",
    rows: [
      [
        {
          key: "outstanding_shares",
          label: "기발행주식수",
          type: "number",
          unit: "주",
          defaultHint: "-",
        },
        {
          key: "conversion_shares",
          label: "행사주식수",
          type: "number",
          unit: "주",
          defaultHint: "-",
        },
      ],
    ],
  },
  {
    id: "ipoRefixing",
    step: 8,
    title: "IPO 리픽싱",
    desc: "IPO 리픽싱 조건을 반영하는 경우 변수를 입력합니다.",
    toggleable: true,
    toggleKey: "is_ipo_refixing",
    rows: [
      [
        {
          key: "ipo_refixing_base",
          label: "IPO리픽싱 기준",
          type: "select",
          options: ["공모가액", "전환가액"],
        },
        {
          key: "ipo_refixing_ratio",
          label: "IPO리픽싱 비율",
          type: "number",
          unit: "%",
          defaultHint: "-",
        },
      ],
      [
        {
          key: "ipo_refixing_start_date",
          label: "IPO리픽싱 시작일",
          type: "date",
        },
        {
          key: "ipo_refixing_end_date",
          label: "IPO리픽싱 종료일",
          type: "date",
        },
      ],
      [
        {
          key: "ipo_refixing_cycle",
          label: "IPO리픽싱 주기",
          type: "number",
          defaultHint: "0",
        },
        {
          key: "ipo_refixing_cycle_interval",
          label: "주기 단위",
          type: "select",
          options: [
            { value: "month", label: "월" },
            { value: "day", label: "일" },
            { value: "year", label: "년" },
          ],
        },
      ],
    ],
  },
];
